import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';

const PAGE_METADATA: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Yachts for Sale in Texas | League City Yacht Sales',
    description:
      'League City Yacht Sales is a full-service yacht brokerage offering luxury power boats and luxury sailboats. You can find our floating office and inventory on pier 27 at Watergate Marina. Give us a call to meet your luxury yachting needs today! Building the Finest Yachting Experience One Deal at a Time.',
  },
  '/about': {
    title: 'About Us | League City Yacht Sales',
    description:
      'Meet the League City Yacht Sales team and learn about our yacht brokerage services in the Texas Gulf Coast area.',
  },
  '/contact': {
    title: 'Contact League City Yacht Sales',
    description:
      'Contact League City Yacht Sales for help buying or selling a yacht in the Houston, Clear Lake and Gulf Coast area.',
  },
  '/yachts': {
    title: 'Yachts for Sale | League City Yacht Sales',
    description:
      'Explore available sailboats and power yachts for sale from League City Yacht Sales.',
  },
  '/merchandises': {
    title: 'Marine Merchandise | League City Yacht Sales',
    description: 'Browse marine merchandise available from League City Yacht Sales.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | League City Yacht Sales',
    description: 'Read the privacy policy for League City Yacht Sales.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | League City Yacht Sales',
    description: 'Read the terms of service for League City Yacht Sales.',
  },
};

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private title = inject(Title);
  private meta = inject(Meta);
  private document = inject(DOCUMENT);

  applyRoute(url: string): void {
    const path = `/${url.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, '')}` || '/';
    const canonical = new URL(path === '/' ? '/' : path, environment.publicUrl).toString();
    const isAdminOrPreview = path === '/admin' || path.startsWith('/admin/') || path === '/maintenance-preview';
    const isBoat = /^\/boats\/\d+(?:\/[^/]+\/[^/]+\/\d{10})?$/.test(path);
    const isMerchandise = /^\/merchandise\/\d+$/.test(path);
    const metadata = PAGE_METADATA[path];

    this.clearSchema();
    this.setRobots(!(isAdminOrPreview || isBoat || isMerchandise || !metadata));

    if (metadata) {
      this.setMeta({ ...metadata, url: canonical });
    } else if (isBoat || isMerchandise) {
      this.setMeta({
        title: isBoat ? 'Yacht for Sale | League City Yacht Sales' : 'Marine Product | League City Yacht Sales',
        description: 'View listing details from League City Yacht Sales.',
        url: canonical,
      });
    } else if (isAdminOrPreview) {
      this.setMeta({ title: 'Staff Portal | League City Yacht Sales' });
      this.removeCanonical();
    } else {
      this.setMeta({ title: 'Page Not Found | League City Yacht Sales' });
      this.removeCanonical();
    }
  }

  setMeta(config: { title?: string; description?: string; image?: string; url?: string; imageWidth?: number; imageHeight?: number; imageType?: string }) {
    if (config.title) {
      this.title.setTitle(config.title);
      this.meta.updateTag({ property: 'og:title', content: config.title });
    }

    if (config.description) {
      this.meta.updateTag({ name: 'description', content: config.description });
      this.meta.updateTag({ property: 'og:description', content: config.description });
    } else {
      this.meta.removeTag("name='description'");
      this.meta.removeTag("property='og:description'");
    }

    if (config.url) this.setCanonical(config.url);

    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
    } else {
      this.meta.removeTag("property='og:image'");
    }
    const imageTags = { 'og:image:secure_url': config.image, 'og:image:width': config.imageWidth,
      'og:image:height': config.imageHeight, 'og:image:type': config.imageType };
    for (const [property, value] of Object.entries(imageTags)) {
      if (value) this.meta.updateTag({ property, content: String(value) });
      else this.meta.removeTag(`property='${property}'`);
    }

    if (config.url) {
      this.meta.updateTag({ property: 'og:url', content: config.url });
    } else {
      this.meta.removeTag("property='og:url'");
    }
  }

  setCanonical(url: string) {
    const head = this.document.getElementsByTagName('head')[0];
    let element: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']") || null;

    if (element == null) {
      element = this.document.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    const canonical = new URL(url, environment.publicUrl);
    canonical.search = '';
    canonical.hash = '';
    element.setAttribute('href', canonical.toString());
  }

  setRobots(indexable: boolean): void {
    this.meta.updateTag({ name: 'robots', content: indexable ? 'index,follow' : 'noindex,nofollow' });
  }

  private removeCanonical(): void {
    this.document.querySelector("link[rel='canonical']")?.remove();
    this.meta.removeTag("property='og:url'");
  }

  private clearSchema(): void {
    this.document.querySelector('script[type="application/ld+json"][id="product-schema"]')?.remove();
  }

  setSchema(schema: any) {
    // Replace product structured data so route changes never leave duplicate schemas.
    const head = this.document.getElementsByTagName('head')[0];
    const existingSchema = this.document.querySelector('script[type="application/ld+json"][id="product-schema"]');
    if (existingSchema) {
      head.removeChild(existingSchema);
    }

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'product-schema';
    // Keep user-entered listing text safe when serialized into initial server HTML.
    script.text = JSON.stringify(schema).replace(/</g, '\\u003c');
    head.appendChild(script);
  }
}
